import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, Element, h, Prop, State } from "@stencil/core"
import { getLanguageHandler, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import {
  getRememberMeTooltipBounds,
  getRememberMeTooltipLayout,
} from "./RememberMeTooltipLayout"
import informationIcon from "./information.svg"

let rememberMeId = 0
const TOOLTIP_VERTICAL_PADDING = 12

/** Shared controlled Remember Me checkbox with an accessible explanatory tooltip. */
@Component({
  tag: "remember-me-control",
  styleUrl: "RememberMe.scss",
})
export class RememberMe {
  @Element() host: HTMLElement

  /** Merchant site preferred locale. */
  @Prop() locale?: string

  /** Current controlled Remember Me choice. */
  @Prop() checked?: boolean

  /** Whether both controls are disabled. */
  @Prop() disabled: boolean = false

  /** Callback called after the checkbox value changes. */
  @Prop() rememberMeOnChange?: (rememberMe: boolean) => void

  @State() tooltipOpen: boolean = false
  @State() tooltipLayout?: { above: boolean; style: Record<string, string> }

  private readonly tooltipId = `remember-me-tooltip-${rememberMeId++}`
  private languageHandler: LanguageHandler
  private tooltipButton?: HTMLButtonElement
  private tooltipElement?: HTMLDivElement
  private measuredTooltipHeight?: number
  private needsTooltipMeasurement = false

  componentWillLoad() {
    this.languageHandler = getLanguageHandler(getSupportedLocaleData(this.locale).lang)
  }

  componentDidLoad() {
    document.addEventListener("pointerdown", this.onDocumentPointerDown)
    document.addEventListener("keydown", this.onDocumentKeyDown)
    document.addEventListener("scroll", this.updateTooltipPosition, true)
    window.addEventListener("resize", this.prepareTooltipMeasurement)
  }

  disconnectedCallback() {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown)
    document.removeEventListener("keydown", this.onDocumentKeyDown)
    document.removeEventListener("scroll", this.updateTooltipPosition, true)
    window.removeEventListener("resize", this.prepareTooltipMeasurement)
  }

  private onDocumentPointerDown = (event: Event) => {
    if (!this.tooltipOpen) return

    const target = event.target as Node
    const isTooltipInteraction =
      this.tooltipButton?.contains(target) || this.tooltipElement?.contains(target)

    if (!isTooltipInteraction) this.tooltipOpen = false
  }

  private onDocumentKeyDown = (event: KeyboardEvent) => {
    if (this.tooltipOpen && event.key === "Escape") {
      this.tooltipOpen = false
    }
  }

  componentDidRender() {
    if (!this.tooltipOpen || !this.needsTooltipMeasurement) return

    this.needsTooltipMeasurement = false
    void Promise.resolve().then(this.updateTooltipPosition)
  }

  private getTooltipMeasurementStyle(): Record<string, string> {
    const bounds = getRememberMeTooltipBounds(window.innerWidth, window.innerHeight)
    const verticalPadding = Math.min(TOOLTIP_VERTICAL_PADDING, bounds.maxHeight / 2)
    return {
      "--tooltip-content-max-height": `${Math.max(0, bounds.maxHeight - verticalPadding * 2)}px`,
      "--tooltip-vertical-padding": `${verticalPadding}px`,
      left: "0px",
      maxHeight: `${bounds.maxHeight}px`,
      top: "0px",
      visibility: "hidden",
      width: `${bounds.width}px`,
    }
  }

  private prepareTooltipMeasurement = () => {
    if (!this.tooltipOpen) return

    this.needsTooltipMeasurement = true
    this.measuredTooltipHeight = undefined
    this.tooltipLayout = undefined
  }

  private updateTooltipPosition = () => {
    if (!this.tooltipOpen || !this.tooltipButton || !this.tooltipElement) return

    const buttonRect = this.tooltipButton.getBoundingClientRect()
    const tooltipRect = this.tooltipElement.getBoundingClientRect()
    if (this.measuredTooltipHeight === undefined) {
      this.measuredTooltipHeight = tooltipRect.height
    }
    const layout = getRememberMeTooltipLayout(
      buttonRect,
      this.measuredTooltipHeight,
      window.innerWidth,
      window.innerHeight
    )
    const verticalPadding = Math.min(TOOLTIP_VERTICAL_PADDING, layout.maxHeight / 2)

    this.tooltipLayout = {
      above: layout.above,
      style: {
        "--tooltip-arrow-left": `${layout.arrowLeft}px`,
        "--tooltip-content-max-height": `${Math.max(0, layout.maxHeight - verticalPadding * 2)}px`,
        "--tooltip-vertical-padding": `${verticalPadding}px`,
        left: `${layout.left}px`,
        maxHeight: `${layout.maxHeight}px`,
        top: `${layout.top}px`,
        width: `${layout.width}px`,
      },
    }
  }

  private toggleTooltip = () => {
    if (this.disabled) return

    this.tooltipOpen = !this.tooltipOpen
    if (this.tooltipOpen) {
      this.needsTooltipMeasurement = true
      this.measuredTooltipHeight = undefined
      this.tooltipLayout = undefined
    }
  }

  render() {
    return (
      <div class="rememberMeControl">
        <consent-checkbox
          locale={this.locale}
          label={this.languageHandler.getString("rememberMeInBrowser")}
          compact={true}
          checked={this.checked}
          disabled={this.disabled}
          consentCheckboxOnChange={this.rememberMeOnChange}
        />
        <div class="tooltipContainer">
          <button
            ref={element => (this.tooltipButton = element)}
            class="tooltipButton"
            type="button"
            disabled={this.disabled}
            aria-label={this.languageHandler.getString("rememberMeMoreInformation")}
            aria-expanded={this.tooltipOpen ? "true" : "false"}
            aria-controls={this.tooltipId}
            aria-describedby={this.tooltipOpen ? this.tooltipId : undefined}
            onClick={this.toggleTooltip}>
            <img class="informationIcon" src={informationIcon} alt="" aria-hidden="true" />
          </button>
          {this.tooltipOpen && (
            <div
              ref={element => (this.tooltipElement = element)}
              class={{
                tooltip: true,
                tooltipAbove: this.tooltipLayout?.above === true,
                tooltipVisible: this.tooltipLayout !== undefined,
              }}
              id={this.tooltipId}
              role="tooltip"
              style={this.tooltipLayout?.style || this.getTooltipMeasurementStyle()}>
              <div class="tooltipContent">
                <p>{this.languageHandler.getString("rememberMeTooltipPrimary")}</p>
                <p>{this.languageHandler.getString("rememberMeTooltipWarning")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}
